(() => {
  if (customElements.get('guided-navigation')) return;

  class GuidedNavigation extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.steps = [...this.querySelectorAll('[data-guided-step]')];
      this.form = this.querySelector('[data-guided-form]');
      this.simpleMode = this.dataset.layoutStyle === 'simplified';
      this.simpleFields = [...this.querySelectorAll('[data-guided-simple-field]')];
      this.simpleSelects = [...this.querySelectorAll('[data-guided-simple-select]')];
      this.progress = this.querySelector('[data-guided-progress]');
      this.backButton = this.querySelector('[data-guided-back]');
      this.nextButton = this.querySelector('[data-guided-next]');
      this.nextText = this.querySelector('[data-guided-next-text]');
      this.results = this.querySelector('[data-guided-results]');
      this.resultLinks = this.querySelector('[data-guided-result-links]');
      this.noResult = this.querySelector('[data-guided-no-result]');
      this.restartButton = this.querySelector('[data-guided-restart]');
      this.answers = {};
      this.currentStep = 0;

      const configElement = this.querySelector('[data-guided-config]');
      try {
        this.config = JSON.parse(configElement?.textContent || '{}');
      } catch (_error) {
        this.config = {};
      }

      if (!this.form) return;
      this.restartButton?.addEventListener('click', () => this.restart());

      if (this.simpleMode) {
        if (!this.simpleSelects.length) return;
        this.form.addEventListener('change', () => this.syncSimpleAnswers());
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.syncSimpleAnswers();
          this.showResults();
        });
        this.syncSimpleAnswers();
        return;
      }

      if (!this.steps.length) return;
      this.form.addEventListener('change', (event) => this.handleAnswer(event));
      this.nextButton?.addEventListener('click', () => this.goNext());
      this.backButton?.addEventListener('click', () => this.goBack());
      this.handleLayoutResize = () => this.updateOptionLayout(this.getActiveSteps()[this.currentStep]);
      if ('ResizeObserver' in window) {
        this.layoutObserver = new ResizeObserver(this.handleLayoutResize);
        this.layoutObserver.observe(this);
      } else {
        window.addEventListener('resize', this.handleLayoutResize);
      }
      this.showStep(0, false);
    }

    disconnectedCallback() {
      this.layoutObserver?.disconnect();
      if (this.handleLayoutResize) window.removeEventListener('resize', this.handleLayoutResize);
    }

    handleAnswer(event) {
      const input = event.target.closest('.guided-navigation__option-input');
      if (!input) return;

      const step = input.closest('[data-guided-step]');
      const stepIndex = this.steps.indexOf(step);
      const key = step.dataset.questionKey;
      const questionAffectsRouting = input.dataset.questionAffectsRouting !== 'false';
      const optionAffectsRouting = input.dataset.optionAffectsRouting !== 'false';
      this.answers[key] = {
        value: input.value,
        label: input.dataset.optionLabel || input.value,
        affectsRouting: questionAffectsRouting && optionAffectsRouting,
        includeInResult: optionAffectsRouting,
      };

      // Changing an earlier answer requires the later path to be confirmed again.
      this.steps.slice(stepIndex + 1).forEach((laterStep) => {
        delete this.answers[laterStep.dataset.questionKey];
        laterStep.querySelectorAll('input:checked').forEach((checkedInput) => {
          checkedInput.checked = false;
        });
      });

      this.updateControls();
    }

    syncSimpleAnswers() {
      this.answers = {};
      this.simpleFields.forEach((field) => {
        const select = field.querySelector('[data-guided-simple-select]');
        const shouldSkip = this.shouldSkipStep(field);
        field.hidden = shouldSkip;
        if (!select) return;
        select.disabled = shouldSkip;
        if (shouldSkip) return;

        const option = select.options[select.selectedIndex];
        if (!option) return;
        const questionAffectsRouting = select.dataset.questionAffectsRouting !== 'false';
        const optionAffectsRouting = option.dataset.optionAffectsRouting !== 'false';
        this.answers[select.dataset.questionKey] = {
          value: option.value,
          label: option.dataset.optionLabel || option.textContent.trim() || option.value,
          affectsRouting: questionAffectsRouting && optionAffectsRouting,
          includeInResult: optionAffectsRouting,
        };
      });
    }

    showStep(index, moveFocus = true) {
      const activeSteps = this.getActiveSteps();
      this.currentStep = Math.max(0, Math.min(index, activeSteps.length - 1));
      this.form.hidden = false;
      this.results.hidden = true;
      this.progress.hidden = false;

      const step = activeSteps[this.currentStep];
      this.steps.forEach((candidate) => {
        candidate.hidden = candidate !== step;
      });

      this.progress.textContent = `${this.currentStep + 1} of ${activeSteps.length}`;
      this.updateControls();
      step.scrollTop = 0;
      requestAnimationFrame(() => this.updateOptionLayout(step));

      if (moveFocus) {
        step.querySelector('input:checked, input')?.focus({ preventScroll: true });
        this.scrollCardIntoView();
      }
    }

    updateControls() {
      const activeSteps = this.getActiveSteps();
      const step = activeSteps[this.currentStep];
      const hasAnswer = Boolean(step?.querySelector('input:checked'));
      if (this.nextButton) this.nextButton.disabled = !hasAnswer;
      if (this.backButton) this.backButton.hidden = this.currentStep === 0;
      if (this.nextText && step) this.nextText.textContent = step.dataset.nextLabel || 'Continue';
    }

    goNext() {
      if (this.nextButton?.disabled) return;
      if (this.currentStep < this.getActiveSteps().length - 1) {
        this.showStep(this.currentStep + 1);
      } else {
        this.showResults();
      }
    }

    goBack() {
      if (this.currentStep > 0) this.showStep(this.currentStep - 1);
    }

    getActiveSteps() {
      return this.steps.filter((step) => !this.shouldSkipStep(step));
    }

    shouldSkipStep(step) {
      const dependencyKey = this.toKey(step.dataset.skipIfKey || '');
      if (!dependencyKey) return false;
      const answer = this.answers[dependencyKey];
      if (!answer) return false;
      const skipValues = String(step.dataset.skipIfValues || '')
        .split(/[\n,]+/)
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);
      return skipValues.includes(String(answer.value || '').trim().toLowerCase());
    }

    updateOptionLayout(step) {
      const options = step?.querySelector('.guided-navigation__options');
      const choices = options ? [...options.children] : [];
      if (!choices.length || !options.offsetWidth) return;
      const firstRowTop = choices[0].offsetTop;
      const hasMultipleRows = choices.some((choice) => Math.abs(choice.offsetTop - firstRowTop) > 1);
      options.classList.toggle('guided-navigation__options--wrapped', hasMultipleRows);
      this.fitOptionText(options, hasMultipleRows);
    }

    fitOptionText(options, useCompactLayout) {
      const labels = [...options.querySelectorAll('.guided-navigation__option-text')];
      labels.forEach((label) => label.style.removeProperty('font-size'));
      if (!useCompactLayout) return;

      labels.forEach((label) => {
        if (!label.clientWidth || label.scrollWidth <= label.clientWidth) return;
        const initialSize = Number.parseFloat(window.getComputedStyle(label).fontSize);
        if (!initialSize) return;
        const fittedSize = Math.max(11, (initialSize * label.clientWidth / label.scrollWidth) - 0.25);
        label.style.fontSize = `${fittedSize.toFixed(2)}px`;
      });
    }

    restart() {
      this.form.reset();
      this.answers = {};
      this.resultLinks.innerHTML = '';
      if (this.simpleMode) {
        this.syncSimpleAnswers();
        this.results.hidden = true;
        this.form.hidden = false;
        this.simpleSelects[0]?.focus({ preventScroll: true });
        this.scrollCardIntoView();
        return;
      }
      this.showStep(0);
    }

    showResults() {
      this.renderResults();
      this.form.hidden = true;
      if (this.progress) this.progress.hidden = true;
      this.results.hidden = false;
      this.results.scrollTop = 0;
      this.results.focus({ preventScroll: true });
      this.scrollCardIntoView();
    }

    renderResults() {
      const matchingRule = this.getMatchingRule();
      const primaryTitleTemplate = matchingRule ? matchingRule.primaryTitle : this.config.primaryTitle;
      const primaryUrlTemplate = matchingRule ? matchingRule.primaryUrl : this.config.primaryUrl;
      const secondaryTitleTemplate = matchingRule ? matchingRule.secondaryTitle : this.config.secondaryTitle;
      const secondaryUrlTemplate = matchingRule ? matchingRule.secondaryUrl : this.config.secondaryUrl;
      const primaryTitle = this.replaceTokens(primaryTitleTemplate || '', false);
      const primaryUrl = this.normaliseUrl(this.replaceTokens(primaryUrlTemplate || '', true));
      const secondaryTitle = this.replaceTokens(secondaryTitleTemplate || '', false);
      const secondaryUrl = this.normaliseUrl(this.replaceTokens(secondaryUrlTemplate || '', true));

      this.resultLinks.innerHTML = '';
      this.addResultLink(primaryTitle, primaryUrl, true);
      this.addResultLink(secondaryTitle, secondaryUrl, false);

      if (!this.resultLinks.children.length) {
        this.addResultLink(this.config.fallbackTitle || '', this.config.fallbackUrl || '', true);
      }

      this.noResult.hidden = this.resultLinks.children.length > 0;
    }

    addResultLink(title, url, primary) {
      if (!title || !url) return;
      const link = document.createElement('a');
      link.className = `guided-navigation__result-link${primary ? ' guided-navigation__result-link--primary' : ''}`;
      link.href = url;
      const label = document.createElement('span');
      label.textContent = title;
      const arrow = document.createElement('span');
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '→';
      link.append(label, arrow);
      this.resultLinks.append(link);
    }

    getMatchingRule() {
      const lines = String(this.config.routingRules || '').replace(/\r/g, '').split('\n');
      for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line || line.startsWith('#')) continue;
        const parts = line.split('|').map((part) => part.trim());
        if (parts.length < 3) continue;
        const conditions = parts[0].split(',').map((condition) => condition.trim()).filter(Boolean);
        const matches = conditions.every((condition) => {
          const separator = condition.indexOf('=');
          if (separator < 1) return false;
          const key = this.toKey(condition.slice(0, separator));
          const expected = condition.slice(separator + 1).trim().toLowerCase();
          const answer = this.answers[key];
          if (answer?.affectsRouting === false) return true;
          const actual = String(answer?.value || '').toLowerCase();
          return expected === '*' || actual === expected;
        });
        if (matches) {
          return {
            primaryTitle: parts[1],
            primaryUrl: parts[2],
            secondaryTitle: parts[3] || '',
            secondaryUrl: parts[4] || '',
          };
        }
      }
      return null;
    }

    replaceTokens(template, forUrl = false) {
      const withConditionalText = String(template || '').replace(
        /\[if:([a-z0-9_-]+)\]([\s\S]*?)\[\/if\]/gi,
        (_match, rawKey, content) => {
          const answer = this.answers[this.toKey(rawKey)];
          if (!answer) return '';
          if (forUrl && answer.affectsRouting === false) return '';
          if (!forUrl && answer.includeInResult === false) return '';
          return content;
        },
      );

      return withConditionalText.replace(/\[([a-z0-9_-]+?)(_label)?\]/gi, (_match, rawKey, labelSuffix) => {
        const answer = this.answers[this.toKey(rawKey)];
        if (!answer) return '';
        if (forUrl && answer.affectsRouting === false) return '';
        if (!forUrl && answer.includeInResult === false) return '';
        return labelSuffix ? answer.label : answer.value;
      }).replace(/\s{2,}/g, ' ').trim();
    }

    normaliseUrl(rawUrl) {
      const value = String(rawUrl || '').trim();
      if (!value) return '';
      if (value.startsWith('#')) return value;

      const cleanPath = (path) => path
        .replace(/-{2,}/g, '-')
        .replace(/\/-/g, '/')
        .replace(/-(?=\/|$)/g, '');

      if (/^https?:/i.test(value)) {
        const urlParts = value.match(/^(https?:\/\/[^/?#]+)([^?#]*)(.*)$/i);
        return urlParts ? `${urlParts[1]}${cleanPath(urlParts[2])}${urlParts[3]}` : value;
      }
      if (value.startsWith('/')) return cleanPath(value);
      const handle = value.toLowerCase()
        .replace(/[^a-z0-9_-]+/g, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-+|-+$/g, '');
      return handle ? `${String(this.config.collectionsBase || '/collections').replace(/\/$/, '')}/${handle}` : '';
    }

    toKey(value) {
      return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    }

    scrollCardIntoView() {
      if (window.matchMedia('(max-width: 719px)').matches) {
        this.querySelector('[data-guided-card]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }

  customElements.define('guided-navigation', GuidedNavigation);
})();
